import { Injectable } from '@angular/core';
import { Store } from '../../../services/store';
import { catchError, map, Observable, of, switchMap, tap } from 'rxjs';
import { BomItem } from '../../../models/bom-item.model';
import { OrdersNewProvider } from './orders-new.provider';
import { OrderHeader } from './models/order.dto';
import { OrderItemData, OrderItemTreeVm, OrderVm } from './models/order-item.view-model';
import { guid } from '../../../utils/guid';
import { toVoid } from '../../../utils/void';
import { parseAmount } from '../../../utils/format-amount';
import { Point } from '../../../models/point';
import { ClientPositionSelected } from '../../client-position-picker/client-position-picker.component';

export interface OrdersNewState {
  loading: boolean;
  /**
   * Set from the route before the order loads, so the page tells a fresh `orders/new`
   * from an existing zlecenie that is still loading.
   */
  orderId: number | 'new' | undefined;
  order: OrderVm | undefined;
  /**
   * The position PODSUMOWANIE is scoped to (0097); undefined shows the whole zlecenie.
   * VM guid, not orderItemId — a placeholder row is selectable before its create call
   * returns (the BOM tree's choice).
   */
  selectedItemId: string | undefined;
  positionPicker: {
    position: Point;
  } | undefined;
}

const INITIAL_STATE: OrdersNewState = {
  loading: false,
  orderId: undefined,
  order: undefined,
  selectedItemId: undefined,
  positionPicker: undefined
};

@Injectable({ providedIn: 'root' })
export class OrdersNewStore extends Store<OrdersNewState> {
  constructor(private provider: OrdersNewProvider) {
    super(INITIAL_STATE);
  }

  init(id: number | 'new'): Observable<void> {
    this.update(state => ({ ...state, orderId: id, loading: id !== 'new' }));

    if (id === 'new') {
      return of(undefined);
    }

    return this.provider.getOrder(id)
      .pipe(
        map(OrderVm.mapModelToViewModel),
        tap(order => {
          this.update(state => ({ ...state, order, loading: false }));
        }),
        toVoid()
      );
  }

  /**
   * The whole header travels each time — the view shows the new values right away and
   * the backend gets the same shape (client-order precedent).
   */
  patchHeader(header: OrderHeader): Observable<void> {
    const orderId = this.state.order?.id;

    if (typeof orderId !== 'number') {
      throw new Error('Cannot edit the header of an order that is not saved yet.');
    }

    this.update(state => ({ ...state, order: { ...state.order!, ...header } }));

    return this.provider.patchOrder(orderId, header);
  }

  searchBomItems(search: string): Observable<BomItem[]> {
    return this.provider.searchBomItems(search);
  }

  /**
   * Snapshots the chosen BOM tree as a new root item, ordered as one piece until the
   * root amount is edited. A fresh page gets its header-only order first (0039); the
   * placeholder row already shown stays in place.
   */
  createNewOrderItem(bomItemId: number, name: string, unit: string): Observable<void> {
    const placeholderId = this.insertPlaceholderItem({
      bomItemId,
      name,
      unit,
      amount: 1,
      clientOrder: null,
      primaryPhotoAttachmentId: null
    });

    return this.ensureOrder()
      .pipe(
        switchMap(orderId => this.provider.createOrderItem(orderId, bomItemId)),
        tap(orderItem => {
          this.replacePlaceholder(placeholderId, OrderItemTreeVm.mapModelToViewModel(orderItem));
        }),
        toVoid()
      );
  }

  /**
   * JIT BOM: the typed name enters the catalog first, then the fresh (leaf) item is
   * added like any other (client-order precedent).
   */
  createBomItemAndAddItem(name: string): Observable<void> {
    return this.provider.createBomItem(name)
      .pipe(
        // a JIT item enters the catalog with the API's default unit
        switchMap(({ bomItemId }) => this.createNewOrderItem(bomItemId, name, 'szt'))
      );
  }

  /**
   * The order bank (0039): a client position becomes a client-sourced root item — the
   * same CreateOrderItem call the client-order page makes, so the zlecenie may mix BOM
   * items with positions of several zamówienia. The placeholder shows the position with
   * its zamówienie right away, and keeps it once the response (which never carries the
   * allocation) replaces it.
   */
  createNewOrderItemFromPosition(selected: ClientPositionSelected): Observable<void> {
    const clientOrder = {
      clientOrderId: selected.clientOrderId,
      orderNumber: selected.orderNumber,
      clientName: selected.clientName
    };

    const placeholderId = this.insertPlaceholderItem({
      bomItemId: undefined,
      name: selected.name,
      unit: selected.unit,
      amount: selected.amount,
      clientOrder,
      primaryPhotoAttachmentId: null
    });

    return this.ensureOrder()
      .pipe(
        switchMap(orderId => this.provider.createOrderItemFromPosition(orderId, selected.clientOrderItemId, selected.amount)),
        tap(orderItem => {
          const orderItemVm = OrderItemTreeVm.mapModelToViewModel(orderItem);

          this.replacePlaceholder(placeholderId, { ...orderItemVm, data: { ...orderItemVm.data, clientOrder } });
        }),
        toVoid()
      );
  }

  // a second click on the selected row returns the summary to the whole order
  toggleSelectedItem(itemId: string): void {
    this.update(state => ({ ...state, selectedItemId: state.selectedItemId === itemId ? undefined : itemId }));
  }

  openPositionPicker(position: Point): void {
    this.update(state => ({ ...state, positionPicker: { position } }));
  }

  closePositionPicker(): void {
    this.update(state => ({ ...state, positionPicker: undefined }));
  }

  /**
   * Changes how many pieces of a top-level item the order is for. Children are shown
   * per-parent (BOM-style), so only the root's amount changes in the view-model;
   * the backend rescales the subtree totals itself.
   */
  updateRootAmount(itemId: string, value: string): Observable<void> {
    const amount = parseAmount(value);
    const item = this.state.order?.items.find(x => x.id === itemId);

    if (!item?.data.orderItemId || typeof this.state.order?.id !== 'number') {
      throw new Error('Cannot change the amount of an item that is not saved yet.');
    }

    if (!(amount > 0)) {
      return of(undefined);
    }

    this.update(state => ({
      ...state,
      order: {
        ...state.order!,
        items: state.order!.items.map(x => x.id === itemId ? { ...x, data: { ...x.data, amount } } : x)
      }
    }));

    return this.provider.patchOrderItemAmount(this.state.order.id, item.data.orderItemId, amount);
  }

  /**
   * Removes a root item with its whole tree. The row goes at once; a refusal (reported
   * progress) brings it back and says why in the console (planner precedent). Removing
   * the selected row returns the summary to the whole order; the restore does not bring
   * the selection back.
   */
  deleteOrderItem(itemId: string): Observable<void> {
    const item = this.state.order?.items.find(x => x.id === itemId);

    if (!item?.data.orderItemId || typeof this.state.order?.id !== 'number') {
      throw new Error('Cannot remove an item that is not saved yet.');
    }

    const previousItems = this.state.order.items;

    this.update(state => ({
      ...state,
      order: {
        ...state.order!,
        items: state.order!.items.filter(x => x.id !== itemId)
      },
      selectedItemId: state.selectedItemId === itemId ? undefined : state.selectedItemId
    }));

    return this.provider.deleteOrderItem(this.state.order.id, item.data.orderItemId)
      .pipe(
        catchError(error => {
          console.error('Nie udało się usunąć pozycji zlecenia', error);
          this.update(state => ({ ...state, order: { ...state.order!, items: previousItems } }));
          return of(undefined);
        })
      );
  }

  /**
   * The saved order's id, creating the order header-only first when the page is still
   * on a fresh `orders/new` — the placeholder rows already shown stay in place.
   */
  private ensureOrder(): Observable<number> {
    const orderId = this.state.order?.id;

    if (typeof orderId === 'number') {
      return of(orderId);
    }

    return this.provider.createOrder()
      .pipe(
        tap(order => {
          this.update(state => ({
            ...state,
            orderId: order.id,
            order: {
              ...OrderVm.mapCreatedToViewModel(order),
              items: state.order?.items ?? []
            }
          }));
        }),
        map(order => order.id)
      );
  }

  // on a fresh page the placeholder also opens the not-yet-saved draft order
  private insertPlaceholderItem(data: OrderItemData): string {
    const id = guid();

    this.update(state => {
      const order = state.order ?? OrderVm.draft();

      return {
        ...state,
        order: {
          ...order,
          items: [...order.items, {
            id,
            data,
            children: []
          }]
        }
      };
    });

    return id;
  }

  // the placeholder's guid stays on the real row, so a selection made during the create call survives it (BOM tree precedent)
  private replacePlaceholder(placeholderId: string, item: OrderVm['items'][number]): void {
    this.update(state => ({
      ...state,
      order: {
        ...state.order!,
        items: state.order!.items.map(x => x.id === placeholderId ? { ...item, id: placeholderId } : x)
      }
    }));
  }
}
